/**
 * FundPullRulesModal — per-org Fund-Pull classification rules manager.
 *
 * Super-admin only. Opens from the Upload Credit File page once an org is
 * selected. Lets ops staff add custom regex patterns that re-classify the
 * org's bank-statement credit/debit lines so the "Funds Pulled" stat is
 * accurate when the bank's vendor description doesn't match the global
 * defaults (BillPortal, Property ACH, etc.).
 *
 * Flow: list → add/edit → test pattern (dry-run against existing lines) →
 * save → reclassify existing lines to apply the new rules.
 */
import { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Loader2, Plus, Pencil, Trash2, RefreshCw, FlaskConical, ChevronLeft,
} from 'lucide-react';
import { toast } from 'sonner';

const fmtC = (v) => v == null
  ? '\u2014'
  : '$' + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const emptyRule = {
  name: '',
  line_type: 'credit',
  pattern: '',
  tag: '',
  is_fund_pull: true,
  is_reversal: false,
  priority: 100,
  enabled: true,
};

export default function FundPullRulesModal({ open, onOpenChange, accountId, orgName }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('list'); // 'list' | 'edit' | 'test'
  const [draft, setDraft] = useState(emptyRule);
  const [editingId, setEditingId] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reclassifying, setReclassifying] = useState(false);

  const fetchRules = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const { data } = await api.get('/bank-statements/rules', { params: { account_id: accountId } });
      setRules(data.rules || []);
    } catch (e) {
      toast.error('Failed to load rules');
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    if (open) {
      setView('list');
      setEditingId(null);
      setDraft(emptyRule);
      setTestResult(null);
      fetchRules();
    }
  }, [open, fetchRules]);

  const startNew = () => {
    setDraft(emptyRule);
    setEditingId(null);
    setTestResult(null);
    setView('edit');
  };

  const startEdit = (r) => {
    setDraft({
      name: r.name,
      line_type: r.line_type,
      pattern: r.pattern,
      tag: r.tag,
      is_fund_pull: !!r.is_fund_pull,
      is_reversal: !!r.is_reversal,
      priority: r.priority,
      enabled: !!r.enabled,
    });
    setEditingId(r.id);
    setTestResult(null);
    setView('edit');
  };

  const onTest = async () => {
    if (!draft.pattern) {
      toast.error('Enter a pattern first');
      return;
    }
    setTesting(true);
    try {
      const { data } = await api.post('/bank-statements/rules/test', {
        account_id: accountId,
        pattern: draft.pattern,
        line_type: draft.line_type,
        limit: 25,
      });
      setTestResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Pattern test failed');
    } finally {
      setTesting(false);
    }
  };

  const onSave = async () => {
    if (!draft.name.trim() || !draft.pattern.trim() || !draft.tag.trim()) {
      toast.error('Name, pattern, and tag are required');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/bank-statements/rules/${editingId}`, draft);
        toast.success('Rule updated');
      } else {
        await api.post('/bank-statements/rules', { ...draft, account_id: accountId });
        toast.success('Rule created');
      }
      setView('list');
      fetchRules();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (r) => {
    if (!window.confirm(`Delete rule "${r.name}"? Existing lines keep their current tag until you re-classify.`)) return;
    try {
      await api.delete(`/bank-statements/rules/${r.id}`);
      toast.success('Rule removed');
      fetchRules();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const onReclassify = async () => {
    if (!window.confirm(`Re-run all rules against every existing bank line for ${orgName}? This updates source_tag + is_fund_pull in place. Safe to repeat.`)) return;
    setReclassifying(true);
    try {
      const { data } = await api.post('/bank-statements/rules/reclassify', { account_id: accountId });
      toast.success(`Scanned ${data.scanned} lines · Updated ${data.updated} · Fund pulls added: ${data.fund_pull_added}, removed: ${data.fund_pull_removed}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reclassify failed');
    } finally {
      setReclassifying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="fund-pull-rules-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {view !== 'list' && (
              <button
                onClick={() => { setView('list'); setTestResult(null); }}
                className="text-muted-foreground hover:text-foreground"
                data-testid="rules-back-btn"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}
            <span>Fund-Pull Rules — {orgName || '\u2014'}</span>
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Custom regex patterns that override the global classifier <em>for this org only</em>.
            Use this when the bank uses vendor descriptions that the default patterns don't recognise.
          </p>
        </DialogHeader>

        {view === 'list' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {rules.length} rule{rules.length === 1 ? '' : 's'} configured.
                Org rules run <strong>before</strong> global defaults.
              </p>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onReclassify}
                  disabled={reclassifying || rules.length === 0}
                  data-testid="rules-reclassify-btn"
                >
                  {reclassifying
                    ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                  Re-classify existing lines
                </Button>
                <Button size="sm" onClick={startNew} data-testid="rules-new-btn">
                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                  New rule
                </Button>
              </div>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-xs" data-testid="rules-table">
                <thead className="bg-muted/30 border-b">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Name</th>
                    <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Type</th>
                    <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Pattern</th>
                    <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Tag</th>
                    <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Flags</th>
                    <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Priority</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">Loading...</td></tr>
                  ) : rules.length === 0 ? (
                    <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">
                      No rules yet. This org uses only the global classifier defaults.
                    </td></tr>
                  ) : rules.map((r) => (
                    <tr key={r.id} className="border-b border-border/40 hover:bg-muted/20" data-testid={`rule-row-${r.id}`}>
                      <td className="px-3 py-2 font-medium">{r.name}</td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className="text-[10px]">{r.line_type}</Badge>
                      </td>
                      <td className="px-3 py-2 font-mono text-[10px] truncate max-w-[260px]" title={r.pattern}>
                        {r.pattern}
                      </td>
                      <td className="px-3 py-2 font-mono text-[10px]">{r.tag}</td>
                      <td className="px-3 py-2 space-x-1">
                        {r.is_fund_pull && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">fund pull</Badge>}
                        {r.is_reversal && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]">reversal</Badge>}
                        {!r.enabled && <Badge variant="outline" className="text-[10px] text-muted-foreground">disabled</Badge>}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.priority}</td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => startEdit(r)}
                          className="p-1 text-muted-foreground hover:text-foreground"
                          data-testid={`rule-edit-${r.id}`}
                          title="Edit"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => onDelete(r)}
                          className="p-1 text-muted-foreground hover:text-red-600 ml-1"
                          data-testid={`rule-delete-${r.id}`}
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {view === 'edit' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Name *</label>
                <Input
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="e.g. Multifamily Utility ACH"
                  data-testid="rule-name-input"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Line Type *</label>
                <Select
                  value={draft.line_type}
                  onValueChange={(v) => setDraft({
                    ...draft,
                    line_type: v,
                    is_fund_pull: v === 'credit' ? draft.is_fund_pull : false,
                    is_reversal: v === 'debit' ? draft.is_reversal : false,
                  })}
                >
                  <SelectTrigger data-testid="rule-line-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="credit">Credit</SelectItem>
                    <SelectItem value="debit">Debit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
                  Regex Pattern *
                  <span className="ml-2 font-normal text-muted-foreground normal-case">Case-insensitive; first match wins</span>
                </label>
                <Input
                  value={draft.pattern}
                  onChange={(e) => setDraft({ ...draft, pattern: e.target.value })}
                  placeholder="e.g. \\bMULTIFAMILY\\s+UTIL(ITY)?\\b"
                  className="font-mono text-xs"
                  data-testid="rule-pattern-input"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Tag *</label>
                <Input
                  value={draft.tag}
                  onChange={(e) => setDraft({ ...draft, tag: e.target.value })}
                  placeholder="e.g. multifamily_ach"
                  className="font-mono text-xs"
                  data-testid="rule-tag-input"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Priority</label>
                <Input
                  type="number"
                  value={draft.priority}
                  onChange={(e) => setDraft({ ...draft, priority: Number(e.target.value) || 100 })}
                  data-testid="rule-priority-input"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-6 border rounded-lg p-3 bg-muted/20">
              {draft.line_type === 'credit' && (
                <label className="flex items-center gap-2 text-sm">
                  <Switch
                    checked={draft.is_fund_pull}
                    onCheckedChange={(v) => setDraft({ ...draft, is_fund_pull: v })}
                    data-testid="rule-is-fund-pull"
                  />
                  <span>Counts as Fund Pull</span>
                </label>
              )}
              {draft.line_type === 'debit' && (
                <label className="flex items-center gap-2 text-sm">
                  <Switch
                    checked={draft.is_reversal}
                    onCheckedChange={(v) => setDraft({ ...draft, is_reversal: v })}
                    data-testid="rule-is-reversal"
                  />
                  <span>Reversal (reduces net pull)</span>
                </label>
              )}
              <label className="flex items-center gap-2 text-sm">
                <Switch
                  checked={draft.enabled}
                  onCheckedChange={(v) => setDraft({ ...draft, enabled: v })}
                  data-testid="rule-enabled"
                />
                <span>Enabled</span>
              </label>
            </div>

            <div className="flex items-center justify-between gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={onTest}
                disabled={testing || !draft.pattern}
                data-testid="rule-test-btn"
              >
                {testing
                  ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  : <FlaskConical className="h-3.5 w-3.5 mr-1.5" />}
                Test against existing lines
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setView('list')}>Cancel</Button>
                <Button size="sm" onClick={onSave} disabled={saving} data-testid="rule-save-btn">
                  {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
                  {editingId ? 'Update Rule' : 'Create Rule'}
                </Button>
              </div>
            </div>

            {testResult && (
              <div className="border rounded-lg p-3 bg-card" data-testid="rule-test-result">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm">
                    Matched <strong>{testResult.matched_count}</strong> {draft.line_type} line{testResult.matched_count === 1 ? '' : 's'}
                    {' '} · Total {fmtC(testResult.matched_total)}
                  </p>
                  {testResult.truncated && (
                    <span className="text-[10px] text-muted-foreground">Showing first {testResult.samples.length} samples</span>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[11px]">
                    <thead className="bg-muted/30">
                      <tr>
                        <th className="text-left px-2 py-1.5">Date</th>
                        <th className="text-left px-2 py-1.5">Description</th>
                        <th className="text-right px-2 py-1.5">Amount</th>
                        <th className="text-left px-2 py-1.5">Current tag</th>
                      </tr>
                    </thead>
                    <tbody>
                      {testResult.samples.map((s, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-2 py-1 text-muted-foreground">{s.post_date}</td>
                          <td className="px-2 py-1 truncate max-w-[360px]" title={s.description}>{s.description}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{fmtC(s.amount)}</td>
                          <td className="px-2 py-1 font-mono text-[10px]">{s.current_tag || '\u2014'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
